<template>
  <div class="stripe-deposit-form">
    <!-- Loading state while Stripe.js initializes -->
    <div v-if="loading" class="stripe-loading">
      <span class="spinner"></span>
      <span>{{ $t('player.deposit.stripe_loading') }}</span>
    </div>

    <!-- Stripe init error -->
    <div v-if="initError" class="stripe-error">
      {{ initError }}
    </div>

    <!-- Apple Pay / Google Pay button container -->
    <div v-show="walletAvailable && !loading && !paymentSuccess && !initError">
      <div ref="paymentRequestButtonRef" class="payment-request-button"></div>
      <div class="wallet-divider">
        <span class="wallet-divider-line"></span>
        <span class="wallet-divider-text">{{ $t('player.deposit.stripe_pay_with_card') }}</span>
        <span class="wallet-divider-line"></span>
      </div>
    </div>

    <!-- Payment Element container -->
    <div v-show="!loading && !paymentSuccess && !initError" ref="paymentElementRef" class="payment-element"></div>

    <!-- Payment error -->
    <div v-if="errorMessage && !initError" class="stripe-error">
      {{ errorMessage }}
    </div>

    <!-- Confirm button -->
    <button
      v-if="!loading && !paymentSuccess && !initError"
      class="btn-confirm"
      :disabled="processing"
      @click="handleConfirm"
    >
      <span v-if="processing" class="spinner"></span>
      {{ processing ? $t('common.processing') : $t('player.deposit.stripe_confirm') }}
    </button>

    <!-- Success state -->
    <div v-if="paymentSuccess" class="stripe-success">
      <div class="success-icon">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 6 9 17l-5-5"/>
        </svg>
      </div>
      <h4 class="success-title">{{ $t('player.deposit.stripe_payment_success_title') }}</h4>
      <p class="success-desc">{{ $t('player.deposit.stripe_payment_success_desc') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { loadStripe } from '@stripe/stripe-js'
import type { Stripe, StripeElements, PaymentRequest } from '@stripe/stripe-js'

const props = defineProps<{
  clientSecret: string
  publishableKey: string
  amount: number
  currency: string
}>()

const emit = defineEmits<{
  success: []
  error: [message: string]
}>()

const { t } = useI18n()

const paymentElementRef = ref<HTMLDivElement | null>(null)
const paymentRequestButtonRef = ref<HTMLDivElement | null>(null)
const loading = ref(true)
const processing = ref(false)
const paymentSuccess = ref(false)
const errorMessage = ref<string | null>(null)
const initError = ref<string | null>(null)
const walletAvailable = ref(false)

let stripe: Stripe | null = null
let elements: StripeElements | null = null
let paymentRequestObj: PaymentRequest | null = null

onMounted(async () => {
  try {
    stripe = await loadStripe(props.publishableKey)

    if (!stripe) {
      initError.value = t('player.deposit.stripe_load_error')
      loading.value = false
      return
    }

    // Set up Payment Request (Apple Pay / Google Pay)
    await setupPaymentRequest(stripe)

    elements = stripe.elements({
      clientSecret: props.clientSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#00e701',
          colorBackground: '#213743',
          colorText: '#ffffff',
          colorDanger: '#ed4245',
          fontFamily: 'inherit',
          borderRadius: '8px',
          spacingUnit: '4px',
        },
        rules: {
          '.Input': {
            border: '1px solid rgba(255, 255, 255, 0.07)',
            boxShadow: 'none',
          },
          '.Input:focus': {
            border: '1px solid rgba(20, 117, 225, 0.5)',
            boxShadow: 'none',
          },
        },
      },
    })

    const paymentElement = elements.create('payment')

    if (paymentElementRef.value) {
      paymentElement.mount(paymentElementRef.value)
    }

    paymentElement.on('ready', () => {
      loading.value = false
    })
  } catch {
    initError.value = t('player.deposit.stripe_init_error')
    loading.value = false
  }
})

async function setupPaymentRequest(stripeInstance: Stripe) {
  paymentRequestObj = stripeInstance.paymentRequest({
    country: 'US',
    currency: props.currency.toLowerCase(),
    total: {
      label: t('player.deposit.type_label'),
      amount: props.amount,
    },
    requestPayerName: true,
    requestPayerEmail: true,
  })

  const result = await paymentRequestObj.canMakePayment()

  if (!result) {
    // Device does not support Apple Pay / Google Pay
    return
  }

  walletAvailable.value = true

  // Create a separate elements instance for the PaymentRequestButton (no clientSecret needed)
  const buttonElements = stripeInstance.elements()
  const prButton = buttonElements.create('paymentRequestButton', {
    paymentRequest: paymentRequestObj,
    style: {
      paymentRequestButton: {
        type: 'default',
        theme: 'dark',
        height: '48px',
      },
    },
  })

  if (paymentRequestButtonRef.value) {
    prButton.mount(paymentRequestButtonRef.value)
  }

  // Handle the paymentmethod event from the wallet
  paymentRequestObj.on('paymentmethod', async (ev) => {
    if (!stripe) {
      ev.complete('fail')
      return
    }

    processing.value = true
    errorMessage.value = null

    const { paymentIntent, error } = await stripe.confirmCardPayment(
      props.clientSecret,
      { payment_method: ev.paymentMethod.id },
      { handleActions: false },
    )

    if (error) {
      ev.complete('fail')
      errorMessage.value = error.message ?? t('player.deposit.payment_failed')
      emit('error', errorMessage.value)
      processing.value = false
      return
    }

    if (paymentIntent?.status === 'requires_action') {
      ev.complete('success')
      // Handle 3D Secure if needed after wallet payment
      const { error: actionError, paymentIntent: confirmedIntent } =
        await stripe.confirmCardPayment(props.clientSecret)

      if (actionError) {
        errorMessage.value = actionError.message ?? t('player.deposit.auth_failed')
        emit('error', errorMessage.value)
        processing.value = false
        return
      }

      if (confirmedIntent?.status === 'succeeded') {
        paymentSuccess.value = true
        emit('success')
      } else {
        errorMessage.value = t('player.deposit.payment_not_completed')
        emit('error', errorMessage.value)
      }
    } else if (paymentIntent?.status === 'succeeded') {
      ev.complete('success')
      paymentSuccess.value = true
      emit('success')
    } else {
      ev.complete('fail')
      errorMessage.value = t('player.deposit.payment_not_completed')
      emit('error', errorMessage.value)
    }

    processing.value = false
  })
}

onBeforeUnmount(() => {
  paymentRequestObj = null
  elements = null
  stripe = null
})

async function handleConfirm() {
  if (!stripe || !elements) return

  processing.value = true
  errorMessage.value = null

  const { error, paymentIntent } = await stripe.confirmPayment({
    elements,
    confirmParams: {
      return_url: window.location.href,
    },
    redirect: 'if_required',
  })

  if (error) {
    errorMessage.value = error.message ?? t('player.deposit.payment_failed')
    emit('error', errorMessage.value)
    processing.value = false
    return
  }

  if (paymentIntent?.status === 'succeeded') {
    paymentSuccess.value = true
    emit('success')
  } else if (paymentIntent?.status === 'processing') {
    paymentSuccess.value = true
    emit('success')
  } else {
    errorMessage.value = t('player.deposit.payment_not_completed')
    emit('error', errorMessage.value)
  }

  processing.value = false
}
</script>

<style scoped>
.stripe-deposit-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stripe-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

.payment-request-button {
  min-height: 48px;
}

.wallet-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.wallet-divider-line {
  flex: 1;
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
}

.wallet-divider-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.payment-element {
  min-height: 100px;
}

.stripe-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: rgba(237, 66, 69, 0.08);
  border: 1px solid rgba(237, 66, 69, 0.2);
  border-radius: var(--radius-md, 8px);
  color: var(--color-danger, #ed4245);
  font-size: 13px;
  font-weight: 500;
}

.btn-confirm {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 13px 24px;
  font-size: 15px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  font-family: inherit;
  background: var(--accent-primary, #00e701);
  color: #000;
  transition: all var(--transition-fast, 150ms ease);
}

.btn-confirm:hover:not(:disabled) {
  background: #00cc01;
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-confirm:disabled {
  background: var(--bg-tertiary, #213743);
  color: var(--text-disabled, #4a5568);
  cursor: not-allowed;
  opacity: 0.6;
}

.stripe-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 16px;
}

.success-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.success-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 8px;
}

.success-desc {
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
  line-height: 1.5;
  max-width: 400px;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

.stripe-loading .spinner {
  border-color: rgba(255, 255, 255, 0.2);
  border-top-color: var(--text-secondary, #b1bad3);
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
